import { useCallback } from 'react'
import { WbsItemSearchConditionV2VO } from '../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { useRootWbsItems } from './useRootWbsItems'

type ExcludeResult = {
  cleaned: WbsItemSearchConditionV2VO
  excluded: boolean
}

// 削除されたWBSが rootUuids に残っているとバックエンドが NotFoundException を投げて
// 検索が継続的に失敗するため、検索条件適用時に有効な rootUuid だけに絞り込む。
export const useExcludeInaccessibleRootUuids = (
  functionUuid: string,
  projectUuid: string
) => {
  const getRootWbsItems = useRootWbsItems(projectUuid)

  const exclude = useCallback(
    async (condition: WbsItemSearchConditionV2VO): Promise<ExcludeResult> => {
      if (!condition.rootUuids || condition.rootUuids.length === 0) {
        return { cleaned: condition, excluded: false }
      }
      try {
        const validItems = await getRootWbsItems()
        const validUuids = new Set(validItems.map(item => item.uuid))
        const validRootUuids = condition.rootUuids.filter(uuid =>
          validUuids.has(uuid)
        )
        if (validRootUuids.length === condition.rootUuids.length) {
          return { cleaned: condition, excluded: false }
        }
        return {
          cleaned: {
            ...condition,
            rootUuids: validRootUuids.length > 0 ? validRootUuids : undefined,
          },
          excluded: true,
        }
      } catch {
        // 検証用APIが失敗した場合は元の条件で続行する（フェイルオープン）。
        // 削除済みUUIDが残っていれば検索時に従来通りのエラーが出るが、
        // 検証API障害で画面そのものを使えなくするほうが弊害が大きい。
        return { cleaned: condition, excluded: false }
      }
    },
    [getRootWbsItems]
  )

  const notify = useCallback(() => {
    store.dispatch(
      addScreenMessage(functionUuid, {
        type: MessageLevel.WARN,
        title: intl.formatMessage({
          id: 'wbs.search.rootUuids.excluded.title',
        }),
      })
    )
  }, [functionUuid])

  return { exclude, notify }
}
